import { getStorage } from './Browser';
import { AuthAccountResponse, Token } from '.';
import { formatToken } from '../services/tokenHandler';
import axios, { axiosMock, axiosMockAdapterInstance } from 'services/axios';
import { useFeatureFlagStore } from 'tools/feature-flags/feature-flags.zustand';
import { LDFeatureFlagEnum } from 'tools/feature-flags/launchdarkly-flags';
import getYieldBy from 'resources/get_yield_by.json';
import queryString from 'query-string';
import Integration, {
  AUTH_ACCOUNT_STATUS,
  BASIC_AUTH_PARTNERS,
  ConnectIntegrationResponse,
  LicenseStatusResponse,
  PartnerId,
} from './Integration';
import { DateTime } from 'luxon';
import { AxiosResponse, Method } from 'axios';
import random from 'lodash/random';
import { IntegrationStatus } from '../views/Integrations/components/IntegrationCard/DataStatusIcon/DataStatusIcon';
import { AuthAccount } from 'views/Integrations/types';

const VITE_APP_API_URL = import.meta.env.VITE_APP_API_URL;

export class Valet {
  static readonly BASE_API_URL: string = import.meta.env.VITE_APP_API_URL ?? '';
  static readonly VITE_APP_CROPWISE_API_URL = import.meta.env.VITE_APP_CROPWISE_API_URL ?? '';
}

type IntegrationStatusType = {
  user_id: string;
  org_id: string;
  integrations: {
    partner: string;
    integration_type: string;
    start_time: string;
    modified: string;
    pull_rate?: string | null;
    status: IntegrationStatus;
  }[];
};

type GetAuthAccountResponse = { data: { auth_accounts: AuthAccount[] } };

export const getIntegrations = async (countryCode: string | null) => {
  const post_response = await axios.request({
    method: 'GET',
    url: `${Valet.BASE_API_URL}/valet/v2/connect_integrations`,
    params: { countries: countryCode },
  });
  return post_response?.data as ConnectIntegrationResponse[];
};

export const getOrganizationLicenses = async (org: string | null, accessToken: string) => {
  const response = await axios.get(`${Valet.VITE_APP_CROPWISE_API_URL}/v2/licensing/status`, {
    params: { org, only_valid_contracts: true },
    headers: { authorization: `Bearer ${accessToken}` },
  });
  return response?.data as LicenseStatusResponse;
};

const getRandomStatus = (): IntegrationStatus =>
  ['UPLOADED', 'ERROR', 'UPLOADING', 'PAUSED'][random(0, 3)] as IntegrationStatus;

export const getIntegrationsStatus = async (
  userId: string,
  orgId: string,
): Promise<IntegrationStatusType> => {
  return Promise.resolve<IntegrationStatusType>({
    user_id: userId,
    org_id: orgId,
    integrations: Object.values(PartnerId).map((partner) => ({
      partner: partner,
      integration_type: 'MACHINERY',
      start_time: '2023-01-01T00:00:00.000000+00:00',
      modified: '2023-01-01T00:00:00.000000+00:00',
      pull_rate: null,
      status: getRandomStatus(),
    })),
  });
};

export const saveIntegrationAuthAccount = async (
  user_id: string,
  org_id: string,
  partner_id: string,
  settings: any = {},
  token?: Token,
  integration_id?: string,
) => {
  try {
    const enableV3Auth = useFeatureFlagStore
      .getState()
      .getLaunchDarklyFlag(LDFeatureFlagEnum.ENABLE_V3_AUTH);
    const endpoint = enableV3Auth ? '/auth-service/v3/authaccounts' : '/valet/v2/auth-account';

    const integrationAccountBody: any = {
      user_id,
      org_id,
      partner: partner_id,
      integration_id,
      settings,
    };
    if (token) {
      integrationAccountBody.auth_config = formatToken(token);
    }
    const response = await axios.request({
      method: 'POST',
      url: `${Valet.BASE_API_URL}${endpoint}`,
      data: integrationAccountBody,
    });
    return response.data;
  } catch (err: any) {
    return err;
  }
};

export const updateIntegrationAuthAccount = async (
  user_id: string,
  org_id: string,
  partner_id: string,
  settings?: { is_active?: boolean },
  token?: Token,
  integration_id?: string,
) => {
  try {
    const enableV3Auth = useFeatureFlagStore
      .getState()
      .getLaunchDarklyFlag(LDFeatureFlagEnum.ENABLE_V3_AUTH);
    const endpoint = enableV3Auth ? '/auth-service/v3/authaccounts' : '/valet/v2/auth-account';

    const authConfig = token
      ? {
          auth_config: formatToken(token),
        }
      : {};

    const finalSettings = settings
      ? {
          ...settings,
          is_active: settings.is_active ?? true,
        }
      : {
          is_active: false,
        };

    const data = {
      user_id,
      org_id,
      integration_id,
      partner: partner_id,
      ...authConfig,
      settings: finalSettings,
    };

    const response = await axios.request({
      method: 'PATCH',
      url: `${Valet.BASE_API_URL}${endpoint}`,
      data,
    });

    return response.data;
  } catch (err: any) {
    console.error('PATCH Integration failed:', err);
    return err;
  }
};

export const persistIntegration = async (
  user_id: string,
  org_id: string,
  partner_id: string,
  settings?: any,
  token?: Token,
  integration_id?: string,
) => {
  const enableV3Auth = useFeatureFlagStore
    .getState()
    .getLaunchDarklyFlag(LDFeatureFlagEnum.ENABLE_V3_AUTH);

  const postResponse = await saveIntegrationAuthAccount(
    user_id,
    org_id,
    partner_id,
    settings,
    token,
    integration_id,
  );

  // V3 endpoint handles create/update in single POST, no retry needed
  if (enableV3Auth) {
    return postResponse;
  }

  // V2 endpoint needs PATCH fallback if POST fails
  if (postResponse.error || postResponse.error_type) {
    return await updateIntegrationAuthAccount(
      user_id,
      org_id,
      partner_id,
      settings,
      token,
      integration_id,
    );
  }
  return postResponse;
};

export const getAuthAccounts = async (
  user_id: string,
  org_id: string,
  partner_id?: string,
): Promise<AuthAccount[]> => {
  try {
    const enableV3Auth = useFeatureFlagStore
      .getState()
      .getLaunchDarklyFlag(LDFeatureFlagEnum.ENABLE_V3_AUTH);
    const endpoint = enableV3Auth ? '/auth-service/v3/authaccounts' : '/valet/v2/auth-account';

    const params: { user_id: string; org_id: string; partner_id?: string } = {
      user_id,
      org_id,
      partner_id,
    };

    const response: GetAuthAccountResponse = await axios.get(`${Valet.BASE_API_URL}${endpoint}`, {
      params,
    });
    return response.data.auth_accounts ?? ([] as AuthAccount[]);
  } catch (err: any) {
    return err;
  }
};

export const get_auth_token = async (user_id: string, org_id: string, partner_id: string) => {
  const response = (await getAuthAccounts(user_id, org_id, partner_id)) as any;
  if (response.length) {
    return response[0];
  }
  return response;
};

export const send_oauth_code = async (
  user_id: string,
  org_id: string,
  partner_id: PartnerId,
  redirect_uri: string,
) => {
  try {
    const params = queryString.parse(window.location.search);
    if (!params?.code) {
      const creds = await get_auth_token(user_id, org_id, partner_id);
      if (creds?.auth_config) return creds;
      throw new Error({ ...creds, error_type: 'CREDENTIAL_ERROR' });
    }
    let URL = `${Valet.BASE_API_URL}/valet/v2`;
    switch (partner_id) {
      case PartnerId.JohnDeere:
        URL = `${URL}/johndeere-auth-code`;
        break;
      case PartnerId.CNH:
        URL = `${URL}/cnh-auth-code`;
        break;
      default:
        break;
    }
    const response: any = await axios.request({
      method: 'POST',
      url: URL,
      data: {
        code: params.code,
        redirect_uri,
        org_id: org_id,
        user_id: user_id,
      },
    });
    if (response.data.error) {
      return { error_type: response.data.error, ...response.data };
    }
    return response;
  } catch (err: any) {
    const error_type = 'GET token with code error:';
    console.error(error_type, err);
    return err;
  }
};

export const trigger_ingestion = async (user_id: string, org_id: string, partner_id: string) => {
  try {
    const params: { user_id: string; org_id: string; partner?: string } = {
      user_id,
      org_id,
      partner: partner_id,
    };
    const response: any = await axios.request({
      method: 'GET',
      url: `${Valet.BASE_API_URL}/valet/v2/ingestor-start-job`,
      params,
    });
    return response.data;
  } catch (err: any) {
    if (err?.response?.data?.errors?.length) {
      err.message = err.response.data.errors[0].message;
    }
    const details = 'GET ingestor-start-job failed: ';
    console.error(details, err);
    return err;
  }
};

export const getYieldByData = async () => {
  axiosMockAdapterInstance.onGet(/\/.*yield-by.*/).reply(200, getYieldBy);
  try {
    return await axiosMock.request<any>({
      method: 'GET',
      url: `${VITE_APP_API_URL}/valet/v1/yield-by`,
    });
  } catch (err) {
    console.error('getYieldBy error:', err);
    return undefined;
  }
};

export const getHeatmaps = async (org_id: string, operation_id: string, dtr_code: string) => {
  try {
    const response: any = await axios.post(`${VITE_APP_API_URL}/valet/v2/graphql`, {
      query: `query GetCanonicals {
        images (
          org_id: "${org_id}",
          parent_id: "${operation_id}",
          dtr_code: "${dtr_code}",
          parent_record_type: "UNBUFFERED"
        )
        {
          id
          image_location
          bbox
          palette_entries {
            name
            hex_code
            percent
          }
          dtr_code
          parent_id
          user_id
          org_id
        }
      }`,
    });
    console.log('getCanonicals images:', response);
    return response.data;
  } catch (err: any) {
    console.error('getCanonicalData images error:', err);
    return err;
  }
};

/**
 * Add/Update auth account
 * @param user_id the logged-in user id plus organization id {userId_orgId}
 * @param integration the active integration
 * @param token the integration token
 * @param _forcedApiMethod a forced api Method {PATCH|POST}
 */
export const integrateUser = async (
  user_id: string,
  integration: Integration,
  token: any,
  _forcedApiMethod?: Method,
): Promise<any> => {
  const enableV3Auth = useFeatureFlagStore
    .getState()
    .getLaunchDarklyFlag(LDFeatureFlagEnum.ENABLE_V3_AUTH);
  const endpoint = enableV3Auth ? '/auth-service/v3/authaccounts' : '/valet/v2/auth-account';

  try {
    const { settings } = integration;
    const { auth_config } = token;
    let apiMethod: Method = integration.is_configured ? 'PATCH' : 'POST';
    if (_forcedApiMethod) {
      apiMethod = _forcedApiMethod;
    }
    // V3 always uses POST for create/update
    if (enableV3Auth) {
      apiMethod = 'POST';
    }

    settings.start_date = settings.start_time;
    delete settings.start_time;
    if (!settings.start_date) {
      const dt = DateTime.utc();
      settings.start_date = DateTime.utc(dt.year, dt.month, dt.day, 0, 0).toString();
    }

    if (integration.showAutofft) {
      settings['auto_fft'] = getStorage('auto_fft');
    }

    const [userId, orgId] = user_id.split('_');
    const response: AxiosResponse<AuthAccountResponse> = await axios.request({
      method: apiMethod,
      url: `${VITE_APP_API_URL}${endpoint}`,
      data: {
        user_id: userId,
        org_id: orgId,
        partner: integration.partner_id,
        settings: {
          ...settings,
          with_encryption: BASIC_AUTH_PARTNERS.includes(integration.partner_id),
          status: !integration.is_configured ? AUTH_ACCOUNT_STATUS.INACTIVE : settings.status,
        },
        auth_config: auth_config,
      },
    });
    return response.data;
  } catch (err: any) {
    console.error(err);
    // V3 doesn't need retry logic
    if (
      !enableV3Auth &&
      err.response?.data?.errors?.length > 0 &&
      err.response?.data?.errors[0].message?.indexOf('already exists') > -1
    ) {
      return integrateUser(user_id, integration, token, 'PATCH');
    }
    return Promise.reject(err);
  }
};

export enum CanonicalGraphqlTypes {
  PRESCRIPTIONS = 'prescriptions',
  OPERATIONS = 'operations',
  IMAGES = 'images',
}

export const getAllCanonicalIds = async (
  accessToken: string,
  orgId: string,
  canonicalGraphqlType: CanonicalGraphqlTypes = CanonicalGraphqlTypes.OPERATIONS,
): Promise<{ id: string }[]> => {
  try {
    const response: any = await axios.post(
      `${VITE_APP_API_URL}/valet/v2/graphql`,
      {
        query: `query GetCanonicals {
        ${canonicalGraphqlType} (
          org_id: "${orgId}"
        ) {id}
      }`,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      },
    );
    return response.data.data.operations;
  } catch (err: any) {
    console.error('getAllOperationIds error:', err);
    return err;
  }
};

export default Valet;
